import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IntlModule, IntlService } from '@progress/kendo-angular-intl';
import '@progress/kendo-angular-intl/locales/en/all';
import { ServiceApiService } from '@services/api/service-api.service';
import { NavigationService } from '@services/navigation.service';
import { CustomDateAdapter } from 'app-classic/services/custom-date-adapter';
import player from 'lottie-web';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { LottieComponent, provideLottieOptions } from 'ngx-lottie';
import { NgxMaskModule } from 'ngx-mask';
import { AppSyncService } from '../app/services/app-sync.service';
import { CheckTokensService } from '../app/services/check-tokens.service';
import { ConfigService } from '../app/services/config/config.service';
import { APP_CONFIG } from '../app/services/config/constants';
import { LocaleService } from '../app/services/locale.service';
import { AppClassicRoutingModule } from './app-classic-routing.module';
import { AppClassicComponent } from './app-classic.component';
import { AppClassicMaterialModule } from './app-classic.md-module';
import { AddPetComponent } from './components/booking-steps/add-pet/add-pet.component';
import { AddServiceComponent } from './components/booking-steps/add-service/add-service.component';
import { EnterYourInformationComponent } from './components/booking-steps/enter-your-information/enter-your-information.component';
import { PetInformationComponent } from './components/booking-steps/pet-information/pet-information.component';
import { PetListComponent } from './components/booking-steps/pet-list/pet-list.component';
import { ReviewRequestComponent } from './components/booking-steps/review-request/review-request.component';
import { CustomCalendarHeaderComponent } from './components/booking-steps/select-date-time/custom-calendar-header/custom-calendar-header.component';
import { SelectDateTimeComponent } from './components/booking-steps/select-date-time/select-date-time.component';
import { SelectEmployeeComponent } from './components/booking-steps/select-employee/select-employee.component';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationComponent } from './components/confirmation/confirmation.component';
import { ContentComponent } from './components/content/content.component';
import { GeneralDialogComponent } from './components/dialog/general-dialog.component';
import { ErrorComponent } from './components/error/error.component';
import { TitleBarComponent } from './components/title-bar/title-bar.component';
import { CreateAccountFormComponent } from './components/user-accounts/create-account-form/create-account-form.component';
import { LoginFormComponent } from './components/user-accounts/login-form/login-form.component';
import { ThemeService } from './services/theme.service';
import { SharedDirectivesAndPipesModule } from './shared-directives-and-pipe.module';

@NgModule({
    declarations: [
        AppClassicComponent,
        TitleBarComponent,
        ContentComponent,
        AddServiceComponent,
        SelectEmployeeComponent,
        EnterYourInformationComponent,
        ReviewRequestComponent,
        ConfirmationComponent,
        PetInformationComponent,
        CustomCalendarHeaderComponent,
        ErrorComponent,
        SelectDateTimeComponent,
        LoginFormComponent,
        CreateAccountFormComponent,
        ConfirmationDialogComponent,
        GeneralDialogComponent,
        PetListComponent,
        AddPetComponent,
    ],
    bootstrap: [AppClassicComponent],
    imports: [
        LottieComponent,
        BrowserModule,
        BrowserAnimationsModule,
        AppClassicRoutingModule,
        AppClassicMaterialModule,
        FormsModule,
        ReactiveFormsModule,
        NgxMaskModule.forRoot(),
        IntlModule,
        MatDialogModule,
        RecaptchaV3Module,
        SharedDirectivesAndPipesModule,
    ],
    providers: [
        ConfigService,
        ThemeService,
        NavigationService,
        ServiceApiService,
        CheckTokensService,
        AppSyncService,
        { provide: DateAdapter, useClass: CustomDateAdapter },
        provideAppInitializer(() => {
            const initializerFn = ((themingService: ThemeService) => () => {
                return themingService.initialize();
            })(inject(ThemeService));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = ((checkTokensService: CheckTokensService) => () => {
                return checkTokensService.performCheck();
            })(inject(CheckTokensService));
            return initializerFn();
        }),
        {
            provide: IntlService,
            useExisting: LocaleService,
        },
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            useFactory: (configService: ConfigService) => {
                return configService.config.reCaptchaSiteKey;
            },
            deps: [ConfigService, APP_CONFIG],
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideLottieOptions({
            player: () => player,
        }),
    ],
})
export class AppClassicModule {}
